import { HttpHeaders, HttpResponse, HttpErrorResponse, HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Constants } from '../models/constants.model';
import { OAuthToken } from '../models/oauth-token.model';
import { TokenRequest } from '../models/token-request.model';
import { UrlHelper } from '../utils/url-helper';
import { AppConfigService } from './app-config.service';
import { StorageService } from './storage.service';

@Injectable({
  providedIn: 'root'
})
export class LoginHandlerService {
  static loginInProgess: boolean;

  constructor(private http: HttpClient, private storage: StorageService) { }

  /**
   * Process redirect after authorization on OAuth server
   *
   * @param {OAuthToken} oAuthToken
   * @memberof AuthorizationService
   */
  safeLoginAndRedirect(oAuthToken: OAuthToken) {
    // save tokens
    this.storage.setLoginToken(oAuthToken.access_token);
    this.storage.setRefreshToken(oAuthToken.refresh_token);
    this.storage.setTokenExpire(oAuthToken.getExpirationMilliseconds());

    // redirect to pre-login uri
    const redirect = this.storage.getEntryPointUrl();
    if (redirect !== null) {
      this.storage.setEntryPointUrl(null);
      window.location.href = redirect;
    } else {
      window.location.href = AppConfigService.settings.webBaseUrl;
    }
  }

  /**
   * Fetch access token from OAuth server (Authorization Code Grant with PKCE)
   *
   * @param {string} code
   * @param {string} codeVerifier
   * @return {Observable<OAuthToken>}
   * @memberof TokenService
   */
  public getAccessTokenFromCode(code: string, codeVerifier: string): Observable<HttpResponse<OAuthToken>> {
    var requestObject = new TokenRequest();
    requestObject.code = code;
    requestObject.code_verifier = codeVerifier;

    const url = UrlHelper.trimUrl(AppConfigService.settings.oauthUrl) + '/Authorization/Token';
    const options = { headers: new HttpHeaders(), observe: 'response' } as any;

    return this.http.post<HttpResponse<OAuthToken>>(url, requestObject, options)
      .pipe(
        catchError(
          (error: HttpErrorResponse): Observable<any> => {
            console.error('Error on POST request to ' + url);
            return throwError(error);
          })
      );
  }

  public handleTokenResponse(params: Params): Promise<void> {
    const code = this.getStringOrNull(params, Constants.UrlParams.Code);
    let promise: Promise<OAuthToken>;
    if (code) {
      //Authorization Code Grant
      const codeVerifier = this.storage.getPkceCodeVerifier();
      promise = this.getAccessTokenFromCode(code, codeVerifier)
        .toPromise()
        .then(httpResponse => {
          const payload = httpResponse.body;
          this.storage.setPkceCodeVerifier(null); //delete verifier after request
          const token = new OAuthToken();
          token.access_token = payload.access_token;
          token.refresh_token = payload.refresh_token;
          token.token_type = payload.token_type;
          token.expires_in = Number(payload.expires_in);
          return token
        });
    } else {
      //Implicit Grant
      promise = new Promise<OAuthToken>((resolve) => {
        const token = new OAuthToken();
        token.access_token = this.getStringOrNull(params, Constants.UrlParams.AccessToken);
        token.refresh_token = this.getStringOrNull(params, Constants.UrlParams.RefreshToken);
        token.expires_in = this.getNumberOrNull(params, Constants.UrlParams.ExpiresIn);
        token.token_type = this.getStringOrNull(params, Constants.UrlParams.TokenType);
        resolve(token);
      });
    }
    return promise
      .then(token => this.safeLoginAndRedirect(token))
      .then(() => new Promise<void>(() => {/* never complete */ }));
  }

  private getStringOrNull(object: any, property: string): string | null {
    return object[property] === undefined ? null : object[property];
  }

  private getNumberOrNull(object: any, property: string): number | null {
    return object[property] === undefined ? null : Number(object[property]);
  }
}
